/**
 * Dynamic Fields Service
 * Integrates with backend custom-fields module API
 * Manages dynamic field definitions and values per module/form/record
 *
 * Backend API: /web/custom-fields
 * Backend Module: backend/custom-fields
 */

import apiService from './apiClient';
import logger from '../utils/logger';

// Field Types (matches backend FieldType enum - UPPERCASE values)
export enum DynamicFieldType {
  TEXT = 'TEXT',
  NUMBER = 'NUMBER',
  EMAIL = 'EMAIL',
  PHONE = 'PHONE',
  DATE = 'DATE',
  TIME = 'TIME',
  DATETIME = 'DATETIME',
  BOOLEAN = 'BOOLEAN',
  CHECKBOX = 'CHECKBOX',
  CHECKBOX_GROUP = 'CHECKBOX_GROUP',
  TOGGLE = 'TOGGLE',
  DROPDOWN = 'DROPDOWN',
  MULTI_SELECT = 'MULTI_SELECT',
  RADIO = 'RADIO',
  TEXTAREA = 'TEXTAREA',
  RICHTEXT = 'RICHTEXT',
  FILE = 'FILE',
  IMAGE = 'IMAGE',
  UPLOAD = 'UPLOAD',
  URL = 'URL',
  COLOR = 'COLOR',
}

// Field Configuration
export interface DynamicFieldConfig {
  options?: Array<{ label: string; value: string }>;
  placeholder?: string;
  maxLength?: number;
  min?: number;
  max?: number;
  helpText?: string;
  defaultValue?: any;
}

// Dynamic Field Definition (matches backend CustomFieldEntity)
export interface DynamicField {
  id: string;
  organizationId: number;
  moduleId: string;
  formId: string;
  fieldKey: string;
  fieldLabel: string;
  fieldType: DynamicFieldType;
  fieldConfig?: DynamicFieldConfig;
  isRequired: boolean;
  displayOrder: number;
  isActive: boolean;
  createdBy: string;
  updatedBy?: string;
  createdAt: string;
  updatedAt: string;
}

// DTOs
export interface CreateDynamicFieldDto {
  moduleId: string;
  formId: string;
  fieldKey: string;
  fieldLabel: string;
  fieldType: DynamicFieldType;
  fieldConfig?: DynamicFieldConfig;
  isRequired?: boolean;
  displayOrder?: number;
}

export interface UpdateDynamicFieldDto {
  fieldLabel?: string;
  fieldType?: DynamicFieldType;
  fieldConfig?: DynamicFieldConfig;
  isRequired?: boolean;
  displayOrder?: number;
  isActive?: boolean;
}

class DynamicFieldsService {
  private baseUrl = '/web/custom-fields';

  /**
   * Get fields for specific module and form
   * GET /web/custom-fields/module/:moduleId/form/:formId
   */
  async getFields(moduleId: string, formId: string): Promise<DynamicField[]> {
    try {
      logger.info('Fetching dynamic fields', { moduleId, formId });

      const response = await apiService.get(
        `${this.baseUrl}/module/${moduleId}/form/${formId}`
      );

      return response.data?.fields || response.fields || [];
    } catch (error) {
      logger.error('Failed to fetch dynamic fields', error);
      return [];
    }
  }

  /**
   * Get field values for a record
   * GET /web/custom-fields/module/:moduleId/form/:formId/record/:recordId/values
   */
  async getFieldValues(
    moduleId: string,
    formId: string,
    recordId: string
  ): Promise<Record<string, any>> {
    try {
      logger.info('Fetching field values', { moduleId, formId, recordId });

      const response = await apiService.get(
        `${this.baseUrl}/module/${moduleId}/form/${formId}/record/${recordId}/values`
      );

      return response.data || response || {};
    } catch (error) {
      logger.warn('Failed to fetch field values', error);
      return {};
    }
  }

  /**
   * Set field value for a record
   * POST /web/custom-fields/module/:moduleId/form/:formId/record/:recordId/values
   */
  async setFieldValue(
    moduleId: string,
    formId: string,
    recordId: string,
    fieldKey: string,
    value: any
  ): Promise<void> {
    try {
      logger.info('Setting field value', { moduleId, formId, recordId, fieldKey });

      await apiService.post(
        `${this.baseUrl}/module/${moduleId}/form/${formId}/record/${recordId}/values`,
        { fieldKey, value }
      );
    } catch (error) {
      logger.error('Failed to set field value', error);
      throw error;
    }
  }

  /**
   * Set multiple field values at once
   */
  async setMultipleFieldValues(
    moduleId: string,
    formId: string,
    recordId: string,
    values: Record<string, any>
  ): Promise<void> {
    try {
      logger.info('Setting multiple field values', { moduleId, formId, recordId, count: Object.keys(values).length });

      const promises = Object.entries(values).map(([fieldKey, value]) =>
        this.setFieldValue(moduleId, formId, recordId, fieldKey, value)
      );

      await Promise.all(promises);
    } catch (error) {
      logger.error('Failed to set multiple field values', error);
      throw error;
    }
  }

  /**
   * Create a new dynamic field definition
   * POST /web/custom-fields
   */
  async createField(fieldData: CreateDynamicFieldDto): Promise<DynamicField> {
    try {
      logger.info('Creating dynamic field', { fieldData });

      const response = await apiService.post(this.baseUrl, fieldData);

      return response.data || response;
    } catch (error) {
      logger.error('Failed to create dynamic field', error);
      throw error;
    }
  }

  /**
   * Update dynamic field definition
   * PUT /web/custom-fields/:id
   */
  async updateField(fieldId: string, updateData: UpdateDynamicFieldDto): Promise<DynamicField> {
    try {
      logger.info('Updating dynamic field', { fieldId, updateData });

      const response = await apiService.put(`${this.baseUrl}/${fieldId}`, updateData);

      return response.data || response;
    } catch (error) {
      logger.error('Failed to update dynamic field', error);
      throw error;
    }
  }

  /**
   * Delete dynamic field (soft delete)
   * DELETE /web/custom-fields/:id
   */
  async deleteField(fieldId: string): Promise<void> {
    try {
      logger.info('Deleting dynamic field', { fieldId });

      await apiService.delete(`${this.baseUrl}/${fieldId}`);
    } catch (error) {
      logger.error('Failed to delete dynamic field', error);
      throw error;
    }
  }

  /**
   * Get all fields configuration (default + custom) for config UI
   * GET /web/field-config/:moduleId/:formId
   */
  async getAllFieldsForConfig(moduleId: string, formId: string): Promise<{
    fields: DynamicField[];
    total: number;
    defaultCount: number;
    customCount: number;
  }> {
    try {
      logger.info('Fetching all fields for config', { moduleId, formId });

      const response = await apiService.get(
        `/web/field-config/${moduleId}/${formId}`
      );

      return {
        fields: response.data?.fields || response.fields || [],
        total: response.data?.total || response.total || 0,
        defaultCount: response.data?.defaultCount || response.defaultCount || 0,
        customCount: response.data?.customCount || response.customCount || 0,
      };
    } catch (error) {
      logger.error('Failed to fetch all fields for config', error);
      throw error;
    }
  }
}

export const dynamicFieldsService = new DynamicFieldsService();
export default dynamicFieldsService;
